import React, { useEffect, useState } from 'react';
import { customerService } from '../../../services/customerService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  xeroDirectContactId: string;
  accessToken: string | null;
  setUserAndToken: (user: any, token: string) => void;
  onSaved?: (result: { status: 'active' | 'inactive' | 'obsolete' }) => void;
  readOnly?: boolean;
  initialValues?: {
    credit_terms?: string | null;
    credit_terms_desc?: string | null;
    credit_limit?: number | null;
    credit_limit_desc?: string | null;
    hasIndividualized?: boolean;
  };
}

const CustomerCreditTermsLimitModal: React.FC<Props> = ({ isOpen, onClose, organizationId, xeroDirectContactId, accessToken, setUserAndToken, onSaved, readOnly = false, initialValues }) => {
  const [creditTerms, setCreditTerms] = useState<string>('');
  const [creditTermsDesc, setCreditTermsDesc] = useState<string>('');
  const [creditLimit, setCreditLimit] = useState<string>('');
  const [creditLimitDesc, setCreditLimitDesc] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasIndividualized, setHasIndividualized] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!isOpen) return;
      try {
        setLoading(true);
        setError('');
        if (readOnly && initialValues) {
          setHasIndividualized(Boolean(initialValues.hasIndividualized));
          setCreditTerms(initialValues.credit_terms || '');
          setCreditTermsDesc(initialValues.credit_terms_desc || '');
          setCreditLimit(initialValues.credit_limit != null ? String(initialValues.credit_limit) : '');
          setCreditLimitDesc(initialValues.credit_limit_desc || '');
        } else {
          const data = await customerService.getCustomerCreditTermsLimit(organizationId, xeroDirectContactId, accessToken, setUserAndToken);
          setHasIndividualized(data?.status === 'active');
          setCreditTerms(data?.credit_terms || '');
          setCreditTermsDesc(data?.credit_terms_desc || '');
          setCreditLimit(data?.credit_limit != null ? String(data.credit_limit) : '');
          setCreditLimitDesc(data?.credit_limit_desc || '');
        }
      } catch (e: any) {
        // no active individualized data; ignore
        setHasIndividualized(false);
        setCreditTerms('');
        setCreditTermsDesc('');
        setCreditLimit('');
        setCreditLimitDesc('');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, organizationId, xeroDirectContactId, accessToken, setUserAndToken, readOnly, initialValues]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await customerService.setCustomerCreditTermsLimit(
        organizationId,
        xeroDirectContactId,
        {
          credit_terms: creditTerms || null,
          credit_terms_desc: creditTermsDesc || null,
          credit_limit: creditLimit ? parseFloat(creditLimit) : null,
          credit_limit_desc: creditLimitDesc || null,
        },
        accessToken,
        setUserAndToken
      );
      if (onSaved) onSaved({ status: 'active' });
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToBase = async () => {
    try {
      setSaving(true);
      setError('');
      await customerService.revertCustomerToBaseCredit(
        organizationId,
        xeroDirectContactId,
        accessToken,
        setUserAndToken
      );
      if (onSaved) onSaved({ status: 'inactive' });
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to revert');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Set Customer Credit Terms / Limit</h3>
            {readOnly && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-20">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>
              )}

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Credit Terms</label>
                    <input
                      type="text"
                      value={creditTerms}
                      onChange={(e) => setCreditTerms(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Net 30"
                      maxLength={100}
                    disabled={readOnly}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Credit Terms Description</label>
                    <textarea
                      value={creditTermsDesc}
                      onChange={(e) => setCreditTermsDesc(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={4}
                      placeholder="Describe the credit terms"
                    disabled={readOnly}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Credit Limit</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={creditLimit}
                      onChange={(e) => setCreditLimit(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 100000.00"
                    disabled={readOnly}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Credit Limit Description</label>
                    <textarea
                      value={creditLimitDesc}
                      onChange={(e) => setCreditLimitDesc(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={4}
                      placeholder="Describe the credit limit"
                    disabled={readOnly}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6">
                {hasIndividualized && !readOnly && (
                  <button
                    type="button"
                    onClick={handleRevertToBase}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Revert to Base Credit Terms / Limit
                  </button>
                )}
                <div className="ml-auto flex space-x-3">
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  )}
                  {!readOnly && (
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerCreditTermsLimitModal;
